/*
 * Getters for the [measurements store]{link module:store/measurements}
 *
 * @module store/measurements/getters
 */

import {
  buildInitTree,
  collapseTree,
  createSankeySeriesInorder,
  createSankeyNodeInformation,
  createSankeyNodeInformationForFullTree,
  createSankeySeriesForFullTree,
  addTreeKeyDataAndLevel,
  getMaxDepth
} from '@/helpers/sensor_tree_helpers'
import { sortSelectOptions } from '@/components/helpers'
import i18n from '@/i18n'
import moment from 'moment'
import { getScaledUnit, scaleNumber } from '@/helpers/number_conversion'
import { startOfYearMoment, endOfYearMoment } from '@/store/modules/measurements'
import {
  INSTANT_UPDATE_COUNT_ID_SHIFT,
  PRODUCER_DEVICES,
  SENSOR_FLAG_VALUES,
  SENSOR_TYPE_FLAG_VALUES,
  STATUS_SENSOR_TYPES_FOR_DASHBOARD
} from '@/store/modules/measurements'

const EPSILON = 0.01

export const getSensorBalanceTree = (state) => {
  return () => {
    const tree = buildInitTree(state.sensors)

    tree.prodTree = collapseTree(tree.prodTree)
    tree.consTree = collapseTree(tree.consTree)
    tree.batDmdTree = collapseTree(tree.batDmdTree)
    tree.batSupTree = collapseTree(tree.batSupTree)

    tree.prodTree = addTreeKeyDataAndLevel(tree.prodTree, 0, getMaxDepth(tree.prodTree))
    tree.consTree = addTreeKeyDataAndLevel(tree.consTree, 0, getMaxDepth(tree.consTree))
    tree.batDmdTree = addTreeKeyDataAndLevel(tree.batDmdTree, 0, getMaxDepth(tree.batDmdTree))
    tree.batSupTree = addTreeKeyDataAndLevel(tree.batSupTree, 0, getMaxDepth(tree.batSupTree))

    return tree
  }
}

export const getProdSankeyNodesFromTree = () => {
  return (tree = {}, flipped = false) => {
    return createSankeyNodeInformation(tree.prodTree, flipped ? 'level' : 'invLevel', 0)
  }
}

export const getProdSankeySeriesFromTree = (state) => {
  return (tree = {}, flipped = false) => {
    return createSankeySeriesInorder(state.vals, tree.prodTree, flipped ? 'topDown' : 'bottomUp')
  }
}

export const getConsSankeyNodesFromTree = () => {
  return (tree = {}, flipped = false) => {
    return createSankeyNodeInformation(tree.consTree, flipped ? 'level' : 'invLevel', 0)
  }
}

export const getConsSankeySeriesFromTree = (state) => {
  return (tree = {}, flipped = false) => {
    return createSankeySeriesInorder(state.vals, tree.consTree, flipped ? 'topDown' : 'bottomUp')
  }
}

export const getFullSankeyNodesFromTree = () => {
  return (tree = {}, flipped = false) => {
    return createSankeyNodeInformationForFullTree(tree, flipped)
  }
}

export const getFullSankeySeriesFromTree = (state) => {
  return (tree = {}, flipped = false) => {
    return createSankeySeriesForFullTree(state.vals, tree, flipped)
  }
}

export const sensorIds = (state) => {
  return Object.values(state.sensors).map((s) => s.id)
}

export const getSensorIdsWithFlag = (state) => {
  return ({ sensorFlag }) => {
    return Object.values(state.sensors)
      .filter((sensor) => !sensorFlag || (sensor.flag & sensorFlag) > 0)
      .map((sensor) => sensor.id)
  }
}

export const getSensorTypeIdsWithSensorTypeFlag = (state) => {
  return ({ sensorTypeFlag }) => {
    return [
      ...new Set(
        Object.values(state.sensors)
          .filter((sensor) => !sensorTypeFlag || (sensor.sensorTypeFlag & sensorTypeFlag) > 0)
          .map((sensor) => sensor.sensorTypeId)
      )
    ]
  }
}

export const sensorSids = (state) => {
  return Object.values(state.sensors).map((s) => s.sid)
}

export const getDatetimeRange = (state) => {
  return (locale = 'en') => {
    const datetimeRange = {}
    if (state.vals.datetime_from) {
      const d = new Date(state.vals.datetime_from)
      datetimeRange.datetimeFrom = moment(d)
      datetimeRange.datetimeFrom.local()
      datetimeRange.datetimeFrom.locale(locale)
    }

    if (state.vals.datetime_to) {
      const d = new Date(state.vals.datetime_to)
      datetimeRange.datetimeTo = moment(d)
      datetimeRange.datetimeTo.local()
      datetimeRange.datetimeTo.locale(locale)
    }
    return datetimeRange
  }
}

export const getSensorsForMultiSelect = (state) => {
  return (type, { sensorFlag } = {}) => {
    const sensorOptions = []
    for (const apiKey in state.sensors) {
      const sensor = state.sensors[apiKey]
      if (!checkIfSensorPassesFilter(sensor, { type, sensorFlag })) {
        continue
      }

      sensorOptions.push({
        label: sensor.label,
        value: sensor.id
      })
    }

    return sensorOptions.sort((a, b) =>
      a.label.toUpperCase().localeCompare(b.label.toUpperCase(), undefined, { numeric: true })
    )
  }
}

export const getSensorsForMultiSelectGroupedByLdn = (state) => {
  return (type, { sensorFlag }) => {
    const groupedOptions = []

    for (const apiKey in state.sensors) {
      const sensor = state.sensors[apiKey]
      if (!checkIfSensorPassesFilter(sensor, { type, sensorFlag })) {
        continue
      }

      const opt = {
        value: sensor.id,
        label: sensor.getSensorName()
      }

      const deviceLabel = sensor.getDeviceLabel()

      const group = groupedOptions.filter((group) => group.label === deviceLabel)[0]

      if (group) {
        group.options.push(opt)
      } else {
        groupedOptions.push({
          label: deviceLabel,
          options: [opt]
        })
      }
    }

    const sortBy = (prop) => {
      return (a, b) => {
        return a[prop].localeCompare(b[prop], undefined, { numeric: true })
      }
    }

    groupedOptions.forEach((group) => {
      group.options.sort(sortBy('label'))
    })

    return groupedOptions.sort(sortBy('label'))
  }
}

export const getSensorsForSelect = (state) => {
  return (type) => {
    const sensors = []
    for (const apiKey in state.sensors) {
      const sensor = state.sensors[apiKey]
      if (checkIfSensorPassesFilter(sensor, { type })) {
        sensors.push({
          label: sensor.label,
          value: sensor.id
        })
      }
    }

    sensors.sort((a, b) => {
      const nameA = a.label.toUpperCase()
      const nameB = b.label.toUpperCase()

      if (nameA < nameB) {
        return -1
      }
      return 1
    })

    return sensors
  }
}

export const getValsForTable = (state) => {
  return (forPreview = false) => {
    const res = []
    const keys = Object.keys(state.vals)

    if (keys.length) {
      for (let i = 0; i < state.vals[keys[0]].length; i++) {
        const row = {}
        for (const key of keys) {
          if (state.vals[key][i]) {
            row[key] = state.vals[key][i]
          } else {
            row[key] = ''
          }
        }
        res.push(row)
      }
    }
    if (forPreview) {
      let rowsToDelete = 0

      for (let i = 0; i < Math.floor(res.length / 2); i++) {
        for (let j = Math.ceil(res.length / 2); j < res.length; j++) {
          if (res[i].t_unix === res[j].t_unix) {
            rowsToDelete += 1
          }
        }
      }

      if (rowsToDelete) {
        res.splice(Math.floor(res.length / 2), rowsToDelete + 1)
      }
    }

    return res
  }
}

export const getValColsForTable = (state) => {
  return () => {
    return Object.keys(state.vals)
  }
}

export const getSensorLabels = (state) => {
  return () => {
    const sensors = {}
    for (const key in state.sensors) {
      sensors[state.sensors[key].id] = state.sensors[key].label
    }
    return sensors
  }
}

export const getDevicesForList = (state, getters) => {
  const prepareDeviceForList = (device) => {
    const sensors = []
    for (const id of device.sensors) {
      const sensor = getters.getSensorById(id)
      if (sensor) {
        sensors.push({
          id,
          label: sensor.label
        })
      }
    }

    const devices = []
    for (const id of device.children) {
      if (state.devices[id]) {
        devices.push({
          id,
          label: state.devices[id].label
        })
      }
    }

    return {
      id: device.id,
      label: device.label,
      givenName: device.givenName,
      technicianGivenName: device.technicianGivenName,
      defaultLabel: device.defaultLabel,
      deviceType: device.deviceType,
      devices,
      sensors
    }
  }

  return (flag = null) => {
    const devices = []
    for (const id in state.devices) {
      if (!flag || state.devices[id].flag === flag) {
        devices.push(prepareDeviceForList(state.devices[id]))
      }
    }

    return devices
  }
}

export function getSensorById(state) {
  return (id) => {
    for (const apiKey in state.sensors) {
      if (state.sensors[apiKey].id === id) {
        return state.sensors[apiKey]
      }
    }
    return null
  }
}

export const getMeasurementsForDashboard = (state, getters, rootState) => {
  return (dashboardName) => {
    const vals = {}

    for (const key in state.vals) {
      vals[key] = state.vals[key].value
    }

    return rootState[dashboardName].measurements(vals)
  }
}

export const currentVals = (state) =>
  Object.entries(state.vals).reduce(
    (prev, [key, mm]) => ({
      ...prev,
      [key]: mm.value
    }),
    {}
  )

export const instantUpdateSensorsForNewDashboard = (state, getters) => {
  const deviceTypeToSensors = Object.values(state.sensors).reduce((prev, sensor) => {
    if (sensor.type === 'sensors' && (sensor.flag & SENSOR_FLAG_VALUES.instantUpdate) === 0) return prev

    let deviceType
    let countId

    if (sensor.type === 'sensors') {
      deviceType = sensor.deviceType
      countId = sensor.countId
    } else if (sensor.type === 'status_sensors') {
      if (!STATUS_SENSOR_TYPES_FOR_DASHBOARD.includes(sensor.sensorTypeId)) return prev

      const physicalDeviceId = state.devices[sensor.deviceId]?.children?.[0]
      const physicalDevice = state.devices[physicalDeviceId]
      if (!physicalDevice) {
        if (sensor.sensorTypeId !== 'backup_box_state') return prev

        const newestBackupBoxStateSensorId = getters.newestBackupBoxStateSensor?.id
        if (sensor.id !== newestBackupBoxStateSensorId) return prev

        deviceType = 'grid'
        countId = 1 + INSTANT_UPDATE_COUNT_ID_SHIFT
      } else {
        deviceType = physicalDevice.deviceType
        countId = physicalDevice.countId + INSTANT_UPDATE_COUNT_ID_SHIFT
      }
    } else return prev

    prev[deviceType] ||= {}
    prev[deviceType][countId] ||= {
      sensors: []
    }
    prev[deviceType][countId].label ||= sensor.type === 'sensors' ? sensor.deviceLabel : null
    prev[deviceType][countId].sensors.push({
      apiKey: sensor.apiKey,
      sensorType: sensor.type,
      sensorTypeId: sensor.sensorTypeId,
      unit: sensor.unit
    })

    return prev
  }, {})

  for (const deviceType in deviceTypeToSensors) {
    for (const countId in deviceTypeToSensors[deviceType]) {
      if (deviceTypeToSensors[deviceType][countId].sensors.every((s) => s.sensorType === 'status_sensors'))
        delete deviceTypeToSensors[deviceType][countId]
    }
  }

  return deviceTypeToSensors
}

export const newestBackupBoxStateSensor = (state) =>
  Object.values(state.sensors)
    .filter((s) => s.type === 'status_sensors' && s.sensorTypeId === 'backup_box_state')
    .sort((s1, s2) => (moment(s1.createdAt).isBefore(moment(s2.createdAt)) ? 1 : -1))?.[0]

export const getDashboardConfigSensorOptions = (state, _, rootState) => {
  return (withEmsName = false) => {
    const options = []

    for (const key in state.sensors) {
      options.push({
        label:
          (withEmsName && rootState.energyManagementSystems?.emss?.[state.sensors[key].emsId]?.name
            ? `${rootState.energyManagementSystems.emss[state.sensors[key].emsId].name} `
            : '') + createDashboardConfigSensorLabel(state.sensors[key]),
        value: key
      })
    }
    return sortSelectOptions(options, { sortByProperty: 'label' })
  }
}

const createDashboardConfigSensorLabel = (sensor) => {
  let label = sensor.label
  const t = i18n.global.t

  if (sensor.sensorTypeId.includes('_power')) {
    label += ` ${t('api.general.power')}`
  } else if (sensor.sensorTypeId.includes('_soc')) {
    label += ` ${t('api.general.soc')}`
  } else if (sensor.sensorTypeId.includes('_temperature')) {
    label += ` ${t('api.general.temperature')}`
  }

  return label
}

export const getDevicesForSelect = (state) => {
  return ({ withIecLdn = false, nameAsValue = false }) => {
    const devices = []
    for (const id in state.devices) {
      if (!withIecLdn || state.devices[id].iecLdn) {
        const name = state.devices[id].givenName
        devices.push({ label: name, value: nameAsValue ? name : id })
      }
    }

    return devices
  }
}

export const createdAtYearOfOldestSensor = (state) => {
  const createdAts = Object.values(state.sensors)
    .map((x) => x.createdAt)
    .filter((x) => x !== undefined)

  const years = createdAts.map((x) => moment(x).year())

  return Math.min(...years)
}
// private

/**
 * @function
 *
 * @param {object} sensor
 *
 * @return {boolean}
 */
function checkIfSensorPassesFilter(sensor, { type, sensorFlag }) {
  let hasToBeMeter = true

  switch (type) {
    case 'avg':
      hasToBeMeter = false
      break
    case 'energy':
    case 'power':
    case 'bare_meter':
      break
  }

  if (sensor.isMeter !== hasToBeMeter) {
    return false
  }
  if (sensorFlag && (sensor.flag & sensorFlag) === 0) {
    return false
  }

  return true
}

export const getSensorsSeriesInTimerange = (state) => {
  return ({ sensorIds, from, to }) => {
    const sensorsSeries = {}
    for (const sensorId of sensorIds) {
      if (!state.vals[sensorId]) continue

      const { ts, unit, values } = state.vals[sensorId]
      const momentFrom = moment(from)
      const momentTo = moment(to)
      for (let i = 0; i < ts.length; i++) {
        const momentT = moment(ts[i])
        if (momentT.isBetween(momentFrom, momentTo)) {
          if (!sensorsSeries[sensorId]) {
            sensorsSeries[sensorId] = { ts: [], values: [] }
          }

          sensorsSeries[sensorId].ts.push(momentT)
          sensorsSeries[sensorId].unit = unit
          sensorsSeries[sensorId].values.push(values[i])
        }
      }
    }

    return sensorsSeries
  }
}

export const getMonthlyMeterReadingsItemsForYear = (state, getters) => {
  return ({ sensorIds, year, locale }) => {
    const sensorsValues = getters.getSensorsSeriesInTimerange({
      sensorIds,
      from: startOfYearMoment(year),
      to: endOfYearMoment(year)
    })

    const deviceToSensorsEntries = createDeviceToSensorsEntries(getters, Object.keys(sensorsValues))
    const items = []
    for (const [device, sensors] of deviceToSensorsEntries) {
      items.push({
        name: device,
        _cellProps: { name: { colSpan: 14, class: 'fw-bold' } }
      })

      const deviceItems = []
      for (const sensor of sensors) {
        const scaledUnit = getScaledUnit(sensorsValues[sensor.id].unit)
        const defaultFields = {
          sensorId: sensor.id,
          name: `${sensor.getSensorName()} [${scaledUnit.unit}]`,
          januaryNext: '-'
        }
        for (const month of moment.months()) {
          defaultFields[_.lowerCase(month)] = '-'
        }

        deviceItems.push({
          ...defaultFields,
          ...formattedMonthlyValues(sensorsValues[sensor.id], scaledUnit, year, locale)
        })
      }

      items.push(
        ...deviceItems.sort((a, b) => {
          a.name.localeCompare(b.name, undefined, { numeric: true })
        })
      )
    }

    return items
  }
}

export const getMonthlyMeterReadingsJsonDataForYear = (state, getters) => {
  return ({ sensorIds, year }) => {
    const from = startOfYearMoment(year)
    const to = endOfYearMoment(year)

    const sensorsValues = getters.getSensorsSeriesInTimerange({ sensorIds, from, to })
    const deviceToSensorsEntries = createDeviceToSensorsEntries(getters, Object.keys(sensorsValues))

    const data = []
    for (const [device, sensors] of deviceToSensorsEntries) {
      data.push({ name: device })

      for (const sensor of sensors) {
        const scaledUnit = getScaledUnit(sensorsValues[sensor.id].unit)
        data.push({
          name: `${sensor.getSensorName()} [${scaledUnit.unit}]`,
          ...formattedMonthlyValues(sensorsValues[sensor.id], scaledUnit, year)
        })
      }
    }

    return data
  }
}

const createDeviceToSensorsEntries = (getters, sensorIds) => {
  const deviceToSensors = {}
  for (const sensorId of sensorIds) {
    const sensor = getters.getSensorById(sensorId)

    const deviceLabel = sensor.getDeviceLabel()
    if (!deviceToSensors[deviceLabel]) deviceToSensors[deviceLabel] = []

    deviceToSensors[deviceLabel].push(sensor)
  }

  return Object.entries(deviceToSensors).sort((a, b) => {
    return a[0].localeCompare(b[0], undefined, { numeric: true })
  })
}

const formattedMonthlyValues = (sensorValues, scaledUnit, year, locale = null) => {
  const monthlyValues = {}
  for (const i in sensorValues.ts) {
    const t = moment(sensorValues.ts[i])
    let tMonth = _.lowerCase(moment.months(t.month()))

    if (tMonth === 'january' && t.year() > year) {
      tMonth = 'januaryNext'
    }
    if (sensorValues.values[i] == null) {
      monthlyValues[tMonth] = '-'
      continue
    }

    monthlyValues[tMonth] = scaleNumber(sensorValues.values[i], { scale: scaledUnit.scale, roundPrecision: 3, locale })
  }

  return monthlyValues
}

export const getDeviceOptions = (state) => {
  return ({ onlyPhysicalDevices = false, skippedDeviceIds = [] } = {}) => {
    return Object.values(state.deviceRelations)
      .filter((dR) => {
        if (skippedDeviceIds.includes(parseInt(dR.id))) return false

        if (onlyPhysicalDevices) return dR.devices[0]?.isOfType('physical_device')

        return true
      })
      .map((dR) => ({ label: dR.label, value: dR.id }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
  }
}

export const getSensorOptions = (state) => {
  return ({ skippedSensorIds = [] } = {}) => {
    const buildSensorOptions = (sRs) =>
      sRs
        .filter((sR) => !skippedSensorIds.includes(parseInt(sR.id)))
        .map((sR) => ({ label: sR.label, value: parseInt(sR.id) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))

    return Object.values(state.deviceRelations)
      .map((dR) => ({ label: dR.label, options: buildSensorOptions(dR.sensorRelations) }))
      .filter((dOpt) => dOpt.options.length)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
  }
}

export const deviceIdsToSensorIds = (state) =>
  Object.values(state.deviceRelations).reduce(
    (prev, dR) => ({
      ...prev,
      [parseInt(dR.id)]: dR.sensorRelations.reduce(
        (prev, sR) => {
          if (sR.hasSensorWithType({ type: 'sensors' }))
            return { sensors: [...prev.sensors, parseInt(sR.id)], status_sensors: prev.status_sensors }
          if (sR.hasSensorWithType({ type: 'status_sensors' }))
            return { sensors: prev.sensors, status_sensors: [...prev.status_sensors, parseInt(sR.id)] }
        },
        { sensors: [], status_sensors: [] }
      )
    }),
    {}
  )

export const getSummedMmSeqForTypeIds = (state) => {
  return (sensorTypeIds = []) => {
    const sensorIds = Object.values(state.sensors)
      .filter((s) => sensorTypeIds.includes(s.sensorTypeId))
      .map((s) => s.id)

    const sensorValues = Object.entries(state.seqs)
      .filter(([sensorId]) => sensorIds.includes(sensorId))
      .map(([, vals]) => vals)

    const seq = []
    for (const i in sensorValues[0]) {
      let val = 0
      for (const j in sensorValues) {
        val += sensorValues[j][i] || 0
      }
      seq.push(val)
    }

    return {
      seq,
      times: state.seqs.datetimes
    }
  }
}

export const currentSelfSufficiency = (state) => {
  const gridApiKey = Object.values(state.sensors).find(
    (s) => s.deviceType === 'grid' && (s.flag & SENSOR_FLAG_VALUES.instantUpdate) > 0
  )?.apiKey
  if (!gridApiKey) return 100 // No grid => full self sufficiency

  const gridPower = state.vals[gridApiKey]?.value
  if (gridPower < 0) return 100 // grid-feedin => full self sufficiency

  const producedPower = Object.entries(state.vals).reduce(
    (sum, [apiKey, val]) => (apiKey !== gridApiKey && val.value && val.value < 0 ? (sum += -1 * val.value) : sum),
    0
  )

  return 100 * (producedPower / (producedPower + gridPower))
}

export const selfSufficiency = (_, getters) => {
  const gridDeliveryWh = getters.getSummedMmSeqForTypeIds(['grid_meter_demand'])
  const gridFeedinWh = getters.getSummedMmSeqForTypeIds(['grid_meter_supply'])
  const producerSensorTypeIds = getters.getSensorTypeIdsWithSensorTypeFlag({
    sensorTypeFlag: SENSOR_TYPE_FLAG_VALUES.producer
  })
  const productionWh = getters.getSummedMmSeqForTypeIds(producerSensorTypeIds)

  const selfSupply = gridFeedinWh.seq.map((gVal, i) => productionWh.seq[i] - gVal).map((v) => (v > 0 ? v : 0))

  const selfSufficiency = selfSupply.map((selfSup, i) =>
    selfSup > EPSILON ? selfSup / (selfSup + gridDeliveryWh.seq[i]) : 0
  )

  const totalGridDeliveryWh = gridDeliveryWh.seq.reduce((prev, val) => prev + val, 0)
  const totalSelfSupply = selfSupply.reduce((prev, val) => prev + val, 0)
  const totalSelfSufficiency = totalSelfSupply > EPSILON ? totalSelfSupply / (totalSelfSupply + totalGridDeliveryWh) : 0
  return {
    seq: selfSufficiency.map((s) => s * 100),
    times: gridDeliveryWh.times,
    total: totalSelfSufficiency * 100
  }
}

export const currentFeedin = (state) => {
  const gridApiKey = Object.values(state.sensors).find(
    (s) => s.deviceType === 'grid' && (s.flag & SENSOR_FLAG_VALUES.instantUpdate) > 0
  )?.apiKey
  if (!gridApiKey) return 0 // No grid => No feedin

  const gridPower = state.vals[gridApiKey]?.value
  if (gridPower > 0) return 0 // from-grid => No feedin

  const otherApiKeys = Object.values(state.sensors)
    .filter((s) => s.deviceType !== 'grid' && (s.flag & SENSOR_FLAG_VALUES.instantUpdate) > 0)
    ?.map((s) => s.apiKey)

  const producedPower = otherApiKeys.reduce((sum, apiKey) => {
    const val = state.vals[apiKey]?.value

    return val && val < 0 ? (sum += -1 * val) : sum
  }, 0)

  if (producedPower < EPSILON) return 0
  return (gridPower / producedPower) * -100
}

export const feedin = (_, getters) => {
  const gridFeedinWh = getters.getSummedMmSeqForTypeIds(['grid_meter_supply'])
  const producerSensorTypeIds = getters.getSensorTypeIdsWithSensorTypeFlag({
    sensorTypeFlag: SENSOR_TYPE_FLAG_VALUES.producer
  })
  const productionWh = getters.getSummedMmSeqForTypeIds(producerSensorTypeIds)

  const feedin = gridFeedinWh.seq
    .map((gVal, i) => (productionWh.seq[i] > EPSILON ? gVal / productionWh.seq[i] : 0))
    .map((v) => (v > 1 ? 1 : v))

  const totalGridFeedinWh = gridFeedinWh.seq.reduce((prev, val) => prev + val, 0)
  const totalProductionWh = productionWh.seq.reduce((prev, val) => prev + val, 0)
  const totalFeedin = totalProductionWh > EPSILON ? totalGridFeedinWh / totalProductionWh : 1
  return {
    seq: feedin.map((s) => s * 100),
    times: gridFeedinWh.times,
    total: (totalFeedin > 1 ? 1 : totalFeedin) * 100
  }
}

// pv savings factor => 690 gCO₂/kWh
const PV_SAVINGS_FACTOR = 690
export const emissionSavings = (_, getters) => {
  const pvProdWh = getters.getSummedMmSeqForTypeIds(['pv_meter_supply'])
  const savingsPerKWh = pvProdWh.seq.map((val) => (PV_SAVINGS_FACTOR * (val / 1000)) / 1000)

  return {
    seq: savingsPerKWh,
    times: pvProdWh.times,
    total: savingsPerKWh.reduce((prev, val) => prev + val, 0)
  }
}

export const getMonetarySavings = (_, getters, __, rootGetters) => (emsId) => {
  const gridEmsMetaInfo = rootGetters['emsMetaInfos/getEmsMetaInfo'](emsId, 'GRID_PRICES')
  const fromGridPriceCurrencyPerKWh = gridEmsMetaInfo?.data?.fromGridCurrencyPerKWh
  const toGridPriceCurrencyPerKWh = gridEmsMetaInfo?.data?.toGridCurrencyPerKWh

  if (!fromGridPriceCurrencyPerKWh || !toGridPriceCurrencyPerKWh) {
    return {
      seq: [],
      times: [],
      total: null
    }
  }

  const consumerSensorTypeIds = getters.getSensorTypeIdsWithSensorTypeFlag({
    sensorTypeFlag: SENSOR_TYPE_FLAG_VALUES.consumer
  })
  const consumptionWh = getters.getSummedMmSeqForTypeIds(consumerSensorTypeIds)
  const costsWithoutProductionCurrencyCent = consumptionWh.seq.map(
    (val) => ((val || 0) / 1000) * fromGridPriceCurrencyPerKWh
  )

  const gridDelivery = getters.getSummedMmSeqForTypeIds(['grid_meter_demand'])
  const gridFeedin = getters.getSummedMmSeqForTypeIds(['grid_meter_supply'])
  const actualCostsCurrencyCent = gridDelivery.seq.map(
    (gDel, i) =>
      fromGridPriceCurrencyPerKWh * ((gDel || 0) / 1000) - toGridPriceCurrencyPerKWh * ((gridFeedin.seq[i] || 0) / 1000)
  )

  const savingsCurrency = actualCostsCurrencyCent.map(
    (actualCostCurrencyCent, i) =>
      (costsWithoutProductionCurrencyCent[i] > actualCostCurrencyCent
        ? costsWithoutProductionCurrencyCent[i] - actualCostCurrencyCent
        : 0) / 100
  )
  return {
    seq: savingsCurrency,
    times: consumptionWh.times,
    total: savingsCurrency.reduce((prev, val) => prev + val, 0)
  }
}

export const dashboardStatusSensorIds = (state, getters) => {
  const backupBoxStateSensorId = getters.newestBackupBoxStateSensor?.id

  const statusSensorIds = Object.values(state.sensors)
    .filter(
      (s) =>
        s.type === 'status_sensors' &&
        STATUS_SENSOR_TYPES_FOR_DASHBOARD.includes(s.sensorTypeId) &&
        s.sensorTypeId !== 'backup_box_state'
    )
    .map((s) => s.id)

  return backupBoxStateSensorId ? [...statusSensorIds, backupBoxStateSensorId] : statusSensorIds
}

export const hasProduction = (state) =>
  Object.values(state.sensors).some(
    (s) => PRODUCER_DEVICES.includes(s.deviceType) && (s.sensorTypeFlag & SENSOR_TYPE_FLAG_VALUES.electricPower) > 0
  )

export const wallboxLdnIdToName = (state, getters) =>
  Object.values(state.devices)
    .filter((d) => d?.children?.length === 1 && state.devices[d.children[0]]?.deviceType === 'wallbox')
    .reduce((prev, d) => {
      const label = getters.getFullPhysicalDeviceLabel(d.children[0])
      if (label) {
        prev[`${d.iecLdn}.${d.iecNodePostfix}`] = label
      }
      return prev
    }, {})

export const getFullPhysicalDeviceLabel = (state) => (deviceId) => {
  const device = state.devices[deviceId]
  if (!device) return ''

  if (device.givenName) {
    if (device.technicianGivenName) return `${device.givenName} (${device.technicianGivenName})`

    return `${device.givenName} (${device.defaultLabel})`
  }
  if (device.technicianGivenName) return device.technicianGivenName

  return device.defaultLabel
}
